import { GameConfiguration, Rulebook, RulebookChapter } from './types';
import { vinhosModuleChapters } from './moduleContent';
import { reserve2010Chapters } from './reserve-2010';
import { filterByConfiguration } from './contentFilter';

/**
 * Content Rewrite 3.0 — prose-first textbook for Special Vintage 2016.
 * ONE menu chapter per topic; internal headings only.
 * Printed tile/expert/magnate effect catalogues remain PARTIAL — read from components.
 */
const specialVintage2016Chapters = [
  {
    id: 'before-you-start',
    title: 'Zanim zaczniecie',
    sections: [
      {
        id: 'before-main',
        title: 'Najważniejsza decyzja',
        content: [
          {
            type: 'warning',
            text: 'Status treści: PARTIAL. Procedury Special Vintage 2016 są opisane poniżej. Wartości i efekty nadrukowane wyłącznie na kaflach roczników, ekspertów, magnatów, targów oraz komponentach modułów odczytuj z fizycznych elementów — aplikacja nie wymyśla ich katalogu.',
          },

          { type: 'heading', text: 'Co wybieracie i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Vinhos Deluxe zawiera dwie wersje gry na dwustronnej planszy. Special Vintage 2016 jest krótszą, czytelniejszą stroną przeznaczoną na pierwszą partię. Zachowuje rdzeń ekonomiczny: rozwój posiadłości, produkcję, sprzedaż, eksport i targi wina. Reserve 2010 to pełniejszy, cięższy wariant z własnym bankiem, cechami i targami — nie mieszajcie ich reguł.',
          },
          { type: 'heading', text: 'Kiedy używać tej strony', level: 2 },
          {
            type: 'paragraph',
            text: 'Wybierzcie Special Vintage 2016, gdy uczycie się gry albo chcecie partię o krótszym rytmie decyzji. Reserve 2010 zostawcie na później albo gdy wszyscy znają już 2016.',
          },
          { type: 'heading', text: 'Jak przygotować pierwszą partię', level: 2 },
          {
            type: 'checklist',
            items: [
              'Wybierz stronę planszy Special Vintage 2016 i tę samą stronę plansz graczy.',
              'Na pierwszą partię wyłącz moduły Experts, Islands, Tasting Room i Connoisseur.',
              'Przygotuj około 30 minut na rozłożenie i objaśnienie gry.',
              'Trzymaj Reference Book pod ręką wyłącznie do odczytywania symboli kafli.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład przy stole',
            text: 'Czteroosobowa grupa stawia planszę stroną 2016, wyłącza wszystkie moduły i czyta rozdział „Struktura roku” oraz „Główne akcje” przed pierwszą turą. Reference Book leży obok tylko po to, by ktoś mógł sprawdzić symbol na kaflu, a nie żeby z niego grać całą partię.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: mieszać reguły banku, farmerów albo targów z Reserve 2010, bo plansze wyglądają podobnie.',
          },
          {
            type: 'note',
            text: 'Wyjątek i odsyłacz: jeśli konfigurator w aplikacji ma włączony Reserve 2010, ta instrukcja jest filtrowana i widzisz tylko rozdziały r2010-*. Cel gry opisuje następny rozdział.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Pierwsza partia = strona 2016, bez modułów. P: Czy mogę grać 2016 i 2010 naraz? O: Nie — jeden activeVariantId. P: Czy Reference Book zastępuje tę instrukcję? O: Nie; służy do symboli na komponentach.',
          },
        ],
      },
    ],
  },
  {
    id: 'game-goal',
    title: 'Cel gry',
    sections: [
      {
        id: 'goal-main',
        title: 'Jak wygrać',
        content: [
          { type: 'heading', text: 'Co robisz i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Jesteś portugalskim producentem wina. Przez sześć lat rozwijasz posiadłości, produkujesz wina, sprzedajesz je za bagos i eksportujesz po punkty zwycięstwa. Prestiż budujesz też na trzech targach wina. Wygrywa gracz z największą liczbą VP po końcowym rozliczeniu.',
          },
          { type: 'heading', text: 'Czego nie mylić z celem', level: 2 },
          {
            type: 'bulletList',
            items: [
              'Pieniądze są narzędziem rozwoju, nie głównym wynikiem.',
              'Eksport daje bezpośrednie punkty i pozycjonuje beczki do punktacji końcowej.',
              'Targi dają punkty i dostęp do przywilejów magnatów.',
              'Sama wysoka produkcja bez sprzedaży i eksportu rzadko wygrywa.',
            ],
          },
          {
            type: 'example',
            title: 'Mini-przykład',
            text: 'Masz dużo bagos i słaby eksport. Rywal ma mniej gotówki, ale kilka beczek na wysokich polach eksportu i dobre miejsca na targach. Po końcowym rozliczeniu to on wygrywa — pieniądze nie zastąpiły VP.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: traktować bagos jak punkty 1:1 albo liczyć pozostałe wina jako VP bez podstawy na planszy.',
          },
          {
            type: 'crossReference',
            chapterId: 'final-scoring',
            label: 'Źródła punktów — Punktacja końcowa',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Cel: najwyższe VP po szóstych targach i rozliczeniu. P: Czy wygrywa najbogatszy? O: Nie. P: Czy targi są opcjonalne? O: Nie; trzy targi są częścią rytmu gry.',
          },
        ],
      },
    ],
  },
  {
    id: 'learning-the-economy',
    title: 'Jak czytać gospodarkę wina',
    sections: [
      {
        id: 'learning-economy-main',
        title: 'Od posiadłości do punktów',
        content: [
          { type: 'heading', text: 'Co rozdzielasz i dlaczego', level: 2 },
          {
            type: 'paragraph',
            text: 'Vinhos jest łatwiejsze, gdy nie traktujesz wszystkich liczb jako jednej wartości. Posiadłość tworzy potencjał produkcji. Z tego potencjału powstaje konkretny kafel wina, który może później dojrzeć. Dopiero gotowe wino sprzedajesz, eksportujesz albo wystawiasz na targach. Pieniądze finansują przejście między etapami, a VP są nagrodą za użycie wina we właściwym miejscu.',
          },
          { type: 'heading', text: 'Kiedy nazywać rolę akcji', level: 2 },
          {
            type: 'paragraph',
            text: 'Przed każdą z dwóch akcji roku nazwij głośno jej rolę: rozwój, przygotowanie produkcji, gotówka, eksport albo przygotowanie targów. Jeśli nie potrafisz nazwać roli, sprawdź najpierw, czy akcja przybliża cię do wina, które naprawdę możesz wykorzystać przed końcem gry.',
          },
          {
            type: 'example',
            title: 'Przykład łańcucha',
            text: 'Kupujesz winnicę (potencjał), potem winiarnię i enologa (wyższa wartość produkcji). Na końcu roku produkuje się kafel. W kolejnym roku pozwolisz mu dojrzeć w piwnicy albo sprzedasz/eksportujesz. Nowa winnica sama w sobie nie daje VP.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: próbować eksportować „wartość produkcji” zamiast konkretnego kafla wina albo mylić wartość produkcji z aktualną wartością wina na torze magazynu.',
          },
          {
            type: 'note',
            text: 'Odsyłacz: produkcja i dojrzewanie mają własne rozdziały; trzy wartości (produkcja, wartość wina, Fair Points) wracają w „Plansza i najważniejsze obszary”.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Posiadłość → wino → użycie → VP. P: Czy winnica od razu punktuje? O: Nie. P: Czy wysoka produkcja = wysoka wartość wina? O: Nie; dojrzewanie i renoma mogą zmienić wartość przy użyciu.',
          },
        ],
      },
    ],
  },
  {
    id: 'components',
    title: 'Elementy gry',
    sections: [
      {
        id: 'components-main',
        title: 'Co rozpoznawać',
        content: [
          { type: 'heading', text: 'Co leży na stole i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Zanim wykonasz pierwszą akcję, rozpoznaj elementy, które wracają w każdej turze. Ta lista nie zastępuje diagramu z pudełka — mówi, do czego służy każdy typ komponentu w Special Vintage 2016.',
          },
          {
            type: 'table',
            headers: ['Element', 'Zastosowanie'],
            rows: [
              ['Plansza główna', 'Quadrel akcji, kolejność, lata, sprzedaż, eksport, targi i regiony.'],
              ['Plansza gracza', 'Posiadłości, magazyny/piwnice, bank oraz miejsce na pracowników i kafle.'],
              ['Winnice', 'Tworzą posiadłości i określają kolor produkowanego wina.'],
              ['Winiarnie', 'Podnoszą wartość produkcji posiadłości.'],
              ['Piwnice', 'Pozwalają winom dojrzewać dłużej i zwiększać wartość.'],
              ['Enolodzy i farmerzy', 'Zwiększają wartość produkowanego wina.'],
              ['Beczki', 'Znaczniki sprzedaży, eksportu i przywilejów magnatów.'],
              ['Eksperci winiarscy', 'Jednorazowe zdolności oraz symbole używane podczas targów.'],
              ['Kafle rocznika', 'Pogoda, preferowane cechy targów i parametry danego roku.'],
            ],
          },
          {
            type: 'example',
            title: 'Przykład kontroli',
            text: 'Przed startem każdy gracz ma planszę gracza, znaczniki akcji i kolejności oraz beczki. Wspólnie widzicie Quadrel, tory lat i regiony. Jeśli ktoś nie wie, czym różni się winiarnia od piwnicy, wróćcie do tej tabeli przed pierwszą akcją.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: używać kafli Action/Multiplier z Reserve 2010 albo mieszać stronę planszy gracza 2010 z planszą 2016.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Rozpoznaj Quadrel, posiadłości, wino, beczki i rocznik. P: Czy ten rozdział opisuje każdy efekt eksperta? O: Nie — efekt odczytujesz z kafla. P: Czy limity kafli zależą od liczby graczy? O: Tak; setup to precyzuje.',
          },
        ],
      },
    ],
  },
  {
    id: 'board-overview',
    title: 'Plansza i najważniejsze obszary',
    sections: [
      {
        id: 'board-main',
        title: 'Mapa decyzji',
        content: [
          { type: 'heading', text: 'Co widzisz na planszy', level: 2 },
          {
            type: 'paragraph',
            text: 'Plansza łączy miejsca, w których podejmujesz decyzje. Quadrel wybiera akcję, tory ustalają kolejność i rok, regiony dają winnice i renomę, a obszary sprzedaży, eksportu i targów zamieniają gotowe wino na pieniądze albo punkty.',
          },
          {
            type: 'bulletList',
            items: [
              'Quadrel 3×3: dziewięć pól wyboru akcji.',
              'Tor kolejności: kto wykonuje pierwszą i drugą akcję roku.',
              'Tor lat: sześć rund i momenty targów.',
              'Regiony: renoma i miejsce pozyskiwania winnic.',
              'Rynek sprzedaży: zamiana wina na pieniądze.',
              'Eksport: oddawanie win za punkty.',
              'Targi: deklarowanie wina, Fair Points i magnaci.',
            ],
          },
          { type: 'heading', text: 'Trzy wartości, których nie wolno mieszać', level: 2 },
          {
            type: 'paragraph',
            text: 'Wartość produkcji opisuje potencjał posiadłości w danym roku. Aktualna wartość konkretnego wina odczytujesz z pola magazynu lub piwnicy (oraz ewentualnej renomy przy użyciu). Fair Points służą do rozstrzygania targów — nie są zwykłymi VP.',
          },
          {
            type: 'example',
            title: 'Przykład rozróżnienia',
            text: 'Posiadłość ma wysoką produkcję 7, więc produkuje mocny kafel. To samo wino leży jeszcze na pierwszym polu magazynu — jego aktualna wartość użytkowa jest niższa niż po dojrzewaniu. Fair Points z Press Release to osobny tor, nie „to samo co VP”.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: doliczać Fair Points do VP w trakcie roku albo traktować renomę regionu jako trwałe podniesienie kafla wina.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Plansza = akcje + czas + miejsca użycia wina. P: Czy Fair Points = VP? O: Nie. P: Czy renoma zawsze podnosi kafel? O: Nie; używasz jej zgodnie z regułą danej operacji.',
          },
        ],
      },
    ],
  },
  {
    id: 'setup',
    title: 'Przygotowanie rozgrywki',
    sections: [
      {
        id: 'setup-main',
        title: 'Przygotowanie Special Vintage 2016',
        content: [
          { type: 'heading', text: 'Co setup ustala i dlaczego', level: 2 },
          {
            type: 'paragraph',
            text: 'Przygotowanie ustawia rocznik, dostępne regiony, rynki i limity komponentów zależne od liczby graczy. Błąd tutaj psuje pierwszą produkcję i pierwsze targi. Wykonaj setup przed wyborem pierwszej akcji.',
          },
          { type: 'heading', text: 'Jak przygotować stół', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Połóż planszę stroną Special Vintage 2016 i dopasuj tę samą stronę plansz graczy.',
              'Ułóż kafle rocznika w zakryty stos i odkryj kafel używany dla pierwszego roku zgodnie z ikonografią planszy.',
              'Rozłóż stosy winnic według regionów, kafle winiarni, piwnic, ekspertów, pracowników i kafle targów.',
              'Przygotuj rynki sprzedaży i eksportu tylko w polach dozwolonych dla liczby graczy.',
              'Każdy wybiera kolor i bierze znaczniki akcji, kolejności, punktów, dyski oraz beczki.',
              'Rozmieść znaczniki na torach i planszach zgodnie z diagramem przygotowania z planszy lub Reference Book.',
              'Ustal kolejność początkową i wykonaj początkowe wybory przewidziane przez planszę gracza.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład kontroli gotowości',
            text: 'Przy trzech graczach pola niedostępne dla tej liczby osób są zasłonięte lub usunięte, każdy ma znacznik na torze kolejności, a pierwszy kafel rocznika jest odkryty. Dopiero wtedy ktoś wykonuje pierwszą akcję na Quadrelu.',
          },
          {
            type: 'warning',
            text: 'Liczba dostępnych kafli winiarni i piwnic zależy od liczby graczy. Usuń nadmiar przed rozpoczęciem; nie traktuj zasobów jako nieograniczonych.',
          },
          {
            type: 'note',
            text: 'Dla pierwszej partii nie dodawaj modułów. W aplikacji konfigurator filtruje rozdziały modułowe. Diagram startowy odczytuj z planszy — nie zgaduj pozycji znaczników.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Gotowa partia ma stronę 2016, rocznik, limity graczy i znaczniki na torach. P: Czy mogę zostawić wszystkie winiarnie w puli przy 2 graczach? O: Nie — stosuj limit z przygotowania. P: Czy moduły są wymagane? O: Nie na pierwszą partię.',
          },
        ],
      },
    ],
  },
  {
    id: 'key-concepts',
    title: 'Najważniejsze pojęcia',
    sections: [
      {
        id: 'concepts-main',
        title: 'Słownik mechaniki',
        content: [
          { type: 'heading', text: 'Co musisz znać przed pierwszą akcją', level: 2 },
          {
            type: 'paragraph',
            text: 'Poniższe pojęcia wracają w każdej turze. Jeśli zrozumiesz posiadłość, dwie wartości wina, renomę, bagos i Fair Points, reszta reguł układa się w jeden rytm.',
          },
          {
            type: 'table',
            headers: ['Pojęcie', 'Znaczenie'],
            rows: [
              ['Posiadłość', 'Kolumna na planszy gracza związana z jednym regionem. Musi zawierać co najmniej jedną winnicę.'],
              ['Wartość produkcji', 'Suma wpływu winnic, winiarni, pracowników i pogody w danej posiadłości.'],
              ['Wartość wina', 'Wartość kafla po produkcji i dojrzewaniu. Służy do sprzedaży, eksportu i targów.'],
              ['Renoma regionu', 'Kostki zwiększające wartość wina z danego regionu podczas wybranych operacji.'],
              ['Bagos', 'Waluta używana do kosztów ruchu, budowy, zatrudnienia i utrzymania.'],
              ['Fair Points', 'Punkty targowe używane do rozstrzygania wyniku targów; nie są zwykłymi VP.'],
            ],
          },
          {
            type: 'example',
            title: 'Przykład użycia renomy',
            text: 'Eksportujesz wino o wartości 6 na pole wymagające 7. Legalnie zużywasz jedną kostkę renomy regionu tej posiadłości, aby osiągnąć próg. Renoma nie „wpisuje się” na stałe w kafel — służy tej operacji zgodnie z regułą eksportu.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: mylić renomę ze stałym podniesieniem wartości kafla albo mylić Fair Points z VP zdobywanymi natychmiast przy eksporcie.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Posiadłość produkuje; wartość wina używasz; Fair Points są na targi. P: Czy posiadłość bez winnicy produkuje? O: Nie. P: Czy bagos to VP? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'year-structure',
    title: 'Struktura roku',
    sections: [
      {
        id: 'year-main',
        title: 'Każdy z 6 lat',
        content: [
          { type: 'heading', text: 'Co, kiedy i dlaczego', level: 2 },
          {
            type: 'paragraph',
            text: 'Partia trwa sześć lat. Każdy rok najpierw ustawia kontekst (rocznik, ewentualne odświeżenie), potem daje każdemu graczowi dokładnie dwie główne akcje, a na końcu rozlicza utrzymanie, starzenie i produkcję. Targi wpadają po latach 3., 5. i 6. Ta kolejność wyjaśnia wiele sporów: nowe wino powstaje po akcjach, nie w ich środku.',
          },
          {
            type: 'numberedList',
            items: [
              'Początek roku: od drugiego roku odrzuć poprzedni kafel rocznika i odkryj nowy.',
              'W 4. i 6. roku odwróć ponownie odkryte kafle ekspertów i akcji magnatów.',
              'Pierwsza akcja: każdy gracz wykonuje po jednej akcji zgodnie z kolejnością.',
              'Aktualizacja kolejności, jeżeli ktoś użył pola Pass / Press Release.',
              'Druga akcja: każdy wykonuje po jednej akcji w nowej kolejności.',
              'Ponowna aktualizacja kolejności.',
              'Utrzymanie: każdy gracz z beczkami na rynku sprzedaży zabiera dokładnie jedną z nich.',
              'Starzenie i produkcja win.',
              'Po 3., 5. i 6. roku odbywają się targi.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład pełnego roku',
            text: 'W roku 3 odkrywacie rocznik, każdy robi pierwszą akcję, aktualizujecie kolejność po Passach, każdy robi drugą akcję, zabieracie po jednej beczce ze sprzedaży, starzejecie wina, produkujecie, a następnie rozgrywacie targi — bo to rok targowy.',
          },
          {
            type: 'warning',
            text: 'W każdym roku wykonujesz tylko dwie główne akcje. Darmowe lub dodatkowe efekty kafli nie zmieniają tej podstawowej liczby.',
          },
          {
            type: 'note',
            text: 'Odsyłacz: szczegóły ruchu i pól są w „Główne akcje”; koniec roku ma checklistę w „Koniec roku”.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Dwie akcje → utrzymanie → starzenie → produkcja → ewentualne targi. P: Czy produkuję w środku akcji? O: Nie, na końcu roku. P: Czy w roku bez targów pomijam Press Release? O: Możesz pasować, ale Press Release przygotowuje przyszłe targi.',
          },
        ],
      },
    ],
  },
  {
    id: 'main-actions',
    title: 'Główne akcje i ruch na Quadrelu',
    sections: [
      {
        id: 'quadrel',
        title: 'Ruch',
        content: [
          { type: 'heading', text: 'Co robi ruch i dlaczego', level: 2 },
          {
            type: 'paragraph',
            text: 'Znacznik akcji na Quadrelu wybiera pole, które wykonujesz. Ruch kosztuje czas i pieniądze: sąsiedztwo jest darmowe, dalsze pole i zajęte pola kosztują bagos. Nie wolno wejść na pole, jeśli nie stać cię na pełny ruch i wykonanie akcji.',
          },
          { type: 'heading', text: 'Kiedy liczysz koszt', level: 2 },
          {
            type: 'paragraph',
            text: 'Koszt ruchu liczysz przed przesunięciem znacznika. Najpierw sprawdź sąsiedztwo, potem obecność rywali i znacznik roku/podatku, a na końcu czy po opłacie nadal stać cię na samą akcję. Jeśli nie — wybierz inne pole.',
          },
          {
            type: 'bulletList',
            items: [
              'Musisz przenieść znacznik akcji na inne pole; pozostanie jest zwykle zabronione.',
              'Ruch na pole sąsiadujące ortogonalnie lub po skosie jest bezpłatny.',
              'Ruch na pole niesąsiadujące kosztuje 1 bago do banku.',
              'Płacisz po 1 bago każdemu graczowi, którego znacznik już stoi na docelowym polu.',
              'Jeżeli na polu stoi znacznik roku/podatku, płacisz dodatkowo 1 bago do banku.',
              'Pole Pass / Press Release jest wyjątkiem: wejście na nie nie generuje kosztów ruchu.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład zliczania przed ruchem',
            text: 'Chcesz wejść na odległe pole, na którym stoją dwaj rywale i leży znacznik roku. Liczysz: 1 bago za niesąsiedztwo + 1 + 1 dla rywali + 1 za znacznik roku = 4 bagos ruchu. Dopiero potem sprawdzasz koszt samej akcji. Jeśli masz tylko 4 bagos i akcja też kosztuje, nie wchodzisz.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: przesunąć znacznik, a dopiero potem odkryć, że nie stać na pełny koszt albo na wykonanie akcji.',
          },
          {
            type: 'note',
            text: 'Wyjątek Pass / Press Release nie kasuje innych reguł pola — tylko koszty ruchu. Szczegóły dziewięciu pól są w następnej sekcji tego rozdziału.',
          },
        ],
      },
      {
        id: 'actions-list',
        title: 'Dziewięć pól',
        content: [
          { type: 'heading', text: 'Jakie pola masz do wyboru', level: 2 },
          {
            type: 'table',
            headers: ['Pole', 'Efekt podstawowy'],
            rows: [
              ['Vineyards', 'Kupno winnicy i rozwój posiadłości.'],
              ['Wineries', 'Budowa winiarni.'],
              ['Cellars', 'Budowa piwnicy.'],
              ['Enologists / Farmers', 'Zatrudnienie pracowników.'],
              ['Wine Experts', 'Zatrudnienie ekspertów winiarskich.'],
              ['Sales', 'Sprzedaż wina na rynek krajowy.'],
              ['Exports', 'Eksport wina za punkty.'],
              ['Pass / Press Release', 'Pas, zmiana kolejności lub zapowiedź wina na targi.'],
              ['Vineyards (drugie pole)', 'Drugi dostęp do akcji winnic.'],
            ],
          },
          {
            type: 'example',
            title: 'Przykład kosztu ruchu',
            text: 'Twój znacznik stoi na Sales. Chcesz wejść na Exports, które nie sąsiaduje. Płacisz 1 bago do banku. Na Exports stoi już rywal — płacisz mu 1 bago. Dopiero potem eksportujesz, jeśli stać cię na całość i masz legalne wino.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: zostać na tym samym polu, pominąć opłatę dla graczy na polu albo wejść bez środków na wykonanie akcji.',
          },
          {
            type: 'crossReference',
            chapterId: 'vineyards-regions',
            label: 'Szczegóły winnic',
          },
          {
            type: 'crossReference',
            chapterId: 'wineries',
            label: 'Akcja Winiarnie',
          },
          {
            type: 'crossReference',
            chapterId: 'enologists-farmers',
            label: 'Enolodzy i farmerzy',
          },
          {
            type: 'crossReference',
            chapterId: 'cellars-aging',
            label: 'Piwnice i dojrzewanie',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Zmień pole, zapłać pełny koszt, wykonaj akcję. P: Czy Pass kosztuje ruch? O: Wejście na Pass/Press Release nie generuje kosztów ruchu. P: Czy mogę wejść „na kredyt”? O: Nie — musisz stać na ruch i akcję.',
          },
        ],
      },
    ],
  },
  {
    id: 'vineyards-regions',
    title: 'Winnice i regiony',
    sections: [
      {
        id: 'vineyards-main',
        title: 'Kupno winnicy',
        content: [
          { type: 'heading', text: 'Co daje winnica i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Winnica otwiera lub wzmacnia posiadłość w jednym regionie i ustala kolor produkowanego wina. Bez winnicy nie ma produkcji w danej kolumnie. Region wiąże posiadłość z renomą i ewentualnymi premiami kafla.',
          },
          { type: 'heading', text: 'Jak kupić winnicę', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Wybierz dostępny kafel winnicy z regionu.',
              'Zapłać wskazany koszt.',
              'Jeżeli to pierwszy kafel z regionu, utwórz nową posiadłość w pustej kolumnie planszy gracza.',
              'Jeżeli masz już posiadłość w tym regionie, dołóż winnicę do tej samej posiadłości, o ile pozwalają na to limity.',
              'Zastosuj natychmiastowe premie regionu lub kafla, jeśli występują.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład pierwszej posiadłości',
            text: 'Kupujesz pierwszą winnicę w regionie. Zakładasz nową kolumnę posiadłości, kładziesz winnicę i odnotowujesz kolor. Od tej chwili posiadłość będzie produkować jeden kafel wina tego koloru na koniec roku, o ile osiągnie legalną wartość produkcji.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: mieszać kolory w jednej posiadłości albo zakładać drugą posiadłość w tym samym regionie zamiast dokładać do istniejącej.',
          },
          {
            type: 'note',
            text: 'Kolor winnicy określa, czy posiadłość produkuje wino czerwone, czy białe. Jedna posiadłość pozostaje związana z jednym regionem. Nadrukowane koszty i premie odczytuj z kafla.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Winnica = posiadłość + kolor. P: Czy mogę mieć dwie posiadłości w jednym regionie? O: Nie — dokładasz do tej samej. P: Czy winnica od razu daje VP? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'wineries',
    title: 'Akcja Winiarnie',
    sections: [
      {
        id: 'wineries-main',
        title: 'Budowa winiarni',
        content: [
          { type: 'heading', text: 'Co daje winiarnia i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Winiarnia podnosi wartość produkcji posiadłości i daje miejsce na enologa. Bez winiarni trudniej osiągnąć legalny kafel wina o użytecznej wartości. Budujesz ją akcją Wineries na Quadrelu, gdy masz wolne miejsce w posiadłości i środki na koszt z komponentu.',
          },
          { type: 'heading', text: 'Kiedy budować', level: 2 },
          {
            type: 'paragraph',
            text: 'Buduj winiarnię, gdy posiadasz już winnicę (albo planujesz ją w tym samym roku) i chcesz podnieść produkcję przed końcem roku. Winiarnia nie produkuje sama — potrzebuje winnicy w posiadłości.',
          },
          { type: 'heading', text: 'Jak przebiega akcja', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Wejdź legalnie na pole Wineries i zapłać pełny koszt ruchu.',
              'Wybierz posiadłość z wolnym miejscem na winiarnię.',
              'Zapłać koszt wskazany na kaflu lub planszy gracza.',
              'Połóż winiarnię w posiadłości.',
              'Zastosuj natychmiastowe premie (np. renoma), jeśli komponent je przewiduje.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład przy stole',
            text: 'Masz posiadłość z jedną winnicą i pustym miejscem. Wchodzisz na Wineries, płacisz koszt kafla, kładziesz winiarnię. Przy produkcji na końcu roku doliczasz premię winiarni do wartości produkcji. Później możesz zatrudnić enologa na tę winiarnię.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: budować winiarnię bez wolnego miejsca albo myśleć, że winiarnia sama produkuje wino bez winnicy.',
          },
          {
            type: 'note',
            text: 'Dokładny koszt i limity miejsc odczytuj z komponentów. Odsyłacz: zatrudnianie enologów — rozdział „Enolodzy i farmerzy”; produkcja — „Produkcja wina”.',
          },
          {
            type: 'crossReference',
            chapterId: 'enologists-farmers',
            label: 'Enolodzy i farmerzy',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Winiarnia = wyższa produkcja + slot na enologa. P: Czy winiarnia produkuje bez winnicy? O: Nie. P: Czy koszt jest w aplikacji? O: Nie — odczytaj z kafla/planszy.',
          },
        ],
      },
    ],
  },
  {
    id: 'enologists-farmers',
    title: 'Enolodzy i farmerzy',
    sections: [
      {
        id: 'enologists-main',
        title: 'Zatrudnianie pracowników',
        content: [
          { type: 'heading', text: 'Czym są i po co istnieją', level: 2 },
          {
            type: 'paragraph',
            text: 'Enolodzy i farmerzy to pracownicy przypisani do posiadłości. Podnoszą wartość produkcji na końcu roku. Zatrudniasz ich akcją Enologists / Farmers na Quadrelu. To nie są eksperci winiarscy — eksperci mają osobne pole i cechy targowe.',
          },
          { type: 'heading', text: 'Kiedy zatrudniać', level: 2 },
          {
            type: 'paragraph',
            text: 'Zatrudniaj pracowników, gdy masz legalne miejsce (np. pustą winiarnię na enologa) i chcesz podnieść produkcję przed końcem roku. Farmer zwykle wiąże się z winnicą; enolog z winiarnią — limity miejsc odczytuj z planszy gracza.',
          },
          { type: 'heading', text: 'Jak przebiega akcja', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Wejdź na pole Enologists / Farmers i zapłać koszt ruchu.',
              'Zapłać koszt zatrudnienia wskazany na planszy lub kaflu akcji.',
              'Połóż pracownika na legalnym miejscu w posiadłości.',
              'Nie przekraczaj limitu miejsc na danym typie budynku.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład przy stole',
            text: 'Masz winiarnię bez enologa. Wchodzisz na pole pracowników, płacisz koszt i kładziesz enologa na winiarni. Przy produkcji doliczasz jego wkład. Nie mylisz go z ekspertem z pola Wine Experts.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: mylić enologa z ekspertem winiarskim albo zatrudniać pracownika bez wolnego miejsca w posiadłości.',
          },
          {
            type: 'warning',
            text: 'W Special Vintage 2016 farmerzy są dostępni przez akcję pracowników. Nie przenoś reguły Reserve 2010 (tam farmer bazowo pochodzi z Ribatejo, chyba że włączysz Tasting Room).',
          },
          {
            type: 'crossReference',
            chapterId: 'wine-production',
            label: 'Produkcja wina',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Pracownicy podnoszą produkcję; eksperci to osobna akcja. P: Czy enolog = ekspert? O: Nie. P: Czy mogę brać farmerów w 2016 tą akcją? O: Tak — to nie jest Reserve bez Tasting Room.',
          },
        ],
      },
    ],
  },
  {
    id: 'wine-production',
    title: 'Produkcja wina',
    sections: [
      {
        id: 'production-main',
        title: 'Obliczanie wartości',
        content: [
          { type: 'heading', text: 'Co produkcja robi i kiedy', level: 2 },
          {
            type: 'paragraph',
            text: 'Na końcu roku, po utrzymaniu i starzeniu, każda posiadłość z co najmniej jedną winnicą produkuje dokładnie jeden kafel wina. Wartość produkcji wynika z rozwoju posiadłości i pogody z bieżącego rocznika. To nie jest akcja na Quadrelu.',
          },
          { type: 'heading', text: 'Jak policzyć wartość', level: 2 },
          {
            type: 'bulletList',
            items: [
              'Dodaj wkład winnic w posiadłości.',
              'Dodaj premie winiarni.',
              'Dodaj aktywnych enologów i farmerów przypisanych do posiadłości.',
              'Uwzględnij modyfikator pogody z bieżącego kafla rocznika.',
              'Zastosuj ewentualne efekty ekspertów, kafli i regionu.',
              'Weź kafel wina odpowiedniego koloru i wartości, a następnie połóż go w lewym polu magazynu lub piwnicy tej posiadłości.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład produkcji',
            text: 'Posiadłość ma winnice, winiarnię i enologa. Po zsumowaniu wkładów i pogody otrzymujesz legalną wartość — bierzesz odpowiadający kafel i kładziesz go na pierwszym polu magazynu. Jeśli wynik jest za niski na legalny kafel, posiadłość może nie wyprodukować użytecznego wina; nie zaokrąglaj samodzielnie.',
          },
          {
            type: 'warning',
            text: 'Jeżeli obliczona wartość jest za niska, aby pobrać legalny kafel wina, posiadłość może nie wyprodukować użytecznego wina. Nie zaokrąglaj wartości samodzielnie.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: produkować przed starzeniem, produkować w środku akcji na Quadrelu albo brać kafel „najbliższej” wartości zamiast dokładnie policzonej.',
          },
          {
            type: 'note',
            text: 'W Special Vintage 2016 mówisz o wartości produkcji posiadłości; nie myl jej z „jakością” z terminologii Reserve 2010 ani z aktualną wartością wina na torze magazynu.',
          },
          {
            type: 'crossReference',
            chapterId: 'cellars-aging',
            label: 'Najpierw starzenie — Piwnice i dojrzewanie',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Jedna posiadłość z winnicą → jeden kafel na koniec roku. P: Czy produkuję przed starzeniem? O: Nie. P: Czy mogę „zaokrąglić” w górę? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'cellars-aging',
    title: 'Piwnice i dojrzewanie',
    sections: [
      {
        id: 'cellars-build',
        title: 'Budowa piwnicy',
        content: [
          { type: 'heading', text: 'Co daje piwnica i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Piwnica zastępuje lub rozszerza zwykły magazyn posiadłości i pozwala winom dojrzewać dłużej, osiągając wyższą wartość użytkową. Budujesz ją akcją Cellars na Quadrelu. Piwnica nie podnosi bezpośrednio wartości produkcji — działa przez dojrzewanie.',
          },
          { type: 'heading', text: 'Jak zbudować piwnicę', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Wejdź legalnie na pole Cellars i zapłać koszt ruchu.',
              'Wybierz posiadłość, w której wolno zbudować piwnicę.',
              'Zapłać koszt wskazany na kaflu lub planszy gracza.',
              'Połóż piwnicę tak, by przykryć lub zastąpić magazyn zgodnie z diagramem planszy gracza.',
              'Przenieś istniejące wina z zachowaniem poziomu starzenia — nie „resetuj” dojrzewania przy budowie.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład budowy',
            text: 'Masz wino na pierwszym polu magazynu. Budujesz piwnicę w tej posiadłości i przenosisz wino na odpowiadające pole piwnicy. Wino nie wraca na start toru — zachowuje dotychczasowy poziom dojrzewania.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: resetować wina na lewe pole przy budowie piwnicy albo myśleć, że piwnica od razu podnosi wartość produkcji jak winiarnia.',
          },
        ],
      },
      {
        id: 'aging-main',
        title: 'Starzenie win',
        content: [
          { type: 'heading', text: 'Co robi dojrzewanie i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Dojrzewanie przesuwa istniejące wina w prawo po torze magazynu lub piwnicy, zanim powstanie nowe wino. Wyższe pole oznacza wyższą wartość użytkową. Piwnica wydłuża tor względem zwykłego magazynu.',
          },
          {
            type: 'bulletList',
            items: [
              'Przed produkcją przesuń każde istniejące wino o jedno pole w prawo.',
              'Wino w zwykłym magazynie ma ograniczony czas dojrzewania.',
              'Piwnica rozszerza tor i pozwala osiągnąć wyższą wartość.',
              'Wartość wina odczytujesz z pola, na którym aktualnie leży kafel.',
              'Wina nie mogą zostać przesunięte poza ostatnie legalne pole.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład kolejności',
            text: 'Masz wino na pierwszym polu i budujesz piwnicę w trakcie roku. Na końcu roku najpierw przesuwasz istniejące wino o jedno pole, potem produkujesz nowy kafel na lewe pole. Nie produkujesz najpierw, a potem „dopychasz” stare wina.',
          },
          {
            type: 'commonMistake',
            text: 'Gracze często najpierw produkują nowe wino, a dopiero potem starzeją stare. Prawidłowa kolejność to: starzenie istniejących win, następnie produkcja.',
          },
          {
            type: 'note',
            text: 'Wyjątek: efekty specjalne mogą wskazywać inny moment przesunięcia — wtedy obowiązuje tekst efektu. Rozróżnij wartość produkcji (przy tworzeniu kafla) od wartości wina (pole toru przy użyciu).',
          },
          {
            type: 'crossReference',
            chapterId: 'wine-production',
            label: 'Produkcja wina',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Najpierw budowa (akcja), potem na końcu roku: starzenie, następnie produkcja. P: Czy piwnica automatycznie podnosi produkcję? O: Nie; wydłuża dojrzewanie. P: Czy wino spada poza tor? O: Nie wolno przesuwać poza ostatnie legalne pole — stosuj regułę wypadania z planszy gracza.',
          },
        ],
      },
    ],
  },
  {
    id: 'export',
    title: 'Eksport',
    sections: [
      {
        id: 'export-main',
        title: 'Wysyłka za granicę',
        content: [
          { type: 'heading', text: 'Co daje eksport i kiedy', level: 2 },
          {
            type: 'paragraph',
            text: 'Eksport zamienia gotowe wino na natychmiastowe VP i ustawia beczkę na polu eksportu. To zwykle główne źródło bezpośrednich punktów w Special Vintage 2016. Wykonujesz go akcją Exports na Quadrelu, gdy masz legalne wino i wolne pole progu.',
          },
          {
            type: 'numberedList',
            items: [
              'Wybierz własne wino.',
              'Wybierz wolne pole eksportu dostępne dla liczby graczy.',
              'Wartość wina musi spełniać wymaganie pola.',
              'Możesz zużyć renomę regionu tylko w zakresie dozwolonym przez zasady, aby osiągnąć wymagany próg.',
              'Odrzuć użyte wino.',
              'Umieść beczkę na polu eksportu i natychmiast zdobądź wskazane punkty.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład eksportu',
            text: 'Masz wino o wartości 8. Wolne pole wymaga 8. Oddajesz wino, kładziesz beczkę i bierzesz VP z pola. Beczka zostaje — może mieć znaczenie w punktacji końcowej obszaru eksportu.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: eksportować bez spełnienia progu, używać renomy poza dozwolonym zakresem albo mylić eksport ze sprzedażą krajową.',
          },
          {
            type: 'note',
            text: 'Beczki pozostające w eksporcie mogą mieć znaczenie w końcowej punktacji. Wartości pól odczytuj z planszy dla aktualnej liczby graczy.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Wino + próg → beczka + VP. P: Czy eksport daje bagos? O: Nie — to sprzedaż. P: Czy mogę eksportować „produkcję” bez kafla? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'wine-fairs',
    title: 'Targi wina',
    sections: [
      {
        id: 'fair-press',
        title: 'Press Release',
        content: [
          { type: 'heading', text: 'Co robi Press Release i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Press Release przygotowuje cię do najbliższych targów: deklarujesz jedno wino, zdobywasz Fair Points, zajmujesz stanowisko i możesz sięgnąć po magnatów. Wczesna deklaracja daje wcześniejszy dostęp do stanowiska; późniejsza pozwala wystawić dojrzalsze wino.',
          },
          { type: 'heading', text: 'Kiedy robić Press Release', level: 2 },
          {
            type: 'paragraph',
            text: 'Możesz ogłosić wino wcześniej w latach poprzedzających targi albo dopiero na początku rozstrzygnięcia. Wybór to trade-off: stanowisko i timing versus wartość wina i Fair Points.',
          },
          {
            type: 'numberedList',
            items: [
              'Wybierz wino, które będzie reprezentować cię na najbliższych targach.',
              'Zdobądź punkty targowe wynikające z wartości wina; możesz legalnie wykorzystać renomę regionu.',
              'Zajmij wolne stanowisko targowe i natychmiast otrzymaj jego premię.',
              'Porównaj cechy ekspertów z preferowanymi cechami bieżącego rocznika i odrzuć odpowiednich ekspertów, aby zdobyć dodatkowe punkty targowe.',
              'Jeżeli spełniasz wymagania magnatów, możesz pobrać ich beczki/przywileje zgodnie z limitem.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład wczesnej deklaracji',
            text: 'W roku 2 wchodzisz na Pass / Press Release i deklarujesz wino o przeciętnej wartości, by zająć atrakcyjne stanowisko. Rywal czeka do roku 3 z dojrzalszym winem i wyższymi Fair Points. Oba podejścia są legalne — różnią się ryzykiem utraty stanowiska versus ryzykiem słabszego rankingu.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: deklarować dwa wina na te same targi, liczyć Fair Points jako VP albo pominąć limity magnatów opisane na planszy.',
          },
          {
            type: 'warning',
            text: 'Na każdy targ wystawiasz dokładnie jedno wino. Jeżeli nie ogłosisz go wcześniej, musisz zrobić to na początku targów.',
          },
        ],
      },
      {
        id: 'fair-resolution',
        title: 'Rozstrzygnięcie',
        content: [
          { type: 'heading', text: 'Kiedy i jak rozstrzygasz targi', level: 2 },
          {
            type: 'paragraph',
            text: 'Targi odbywają się po 3., 5. i 6. roku. Gracze porównują zgromadzone Fair Points, stosują premie ekspertów i zdobywają VP według tabeli lub toru danego targu. Remisy rozstrzyga wskazana przez planszę kolejność.',
          },
          {
            type: 'example',
            title: 'Przykład timingów',
            text: 'W roku 2 robisz wczesny Press Release na słabszym winie, by zająć dobre stanowisko. Rywal czeka do tuż przed targami z dojrzalszym winem i wyższymi Fair Points. Oba podejścia są legalne — różnią się ryzykiem i nagrodą stanowiska.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: wystawić więcej niż jedno wino na te same targi albo odświeżyć ekspertów odrzuconych na targi zamiast tylko odwrócić zakryte kafle przed 4. i 6. rokiem.',
          },
          {
            type: 'note',
            text: 'Teksty stanowisk, magnatów i tabel VP odczytuj z planszy oraz kafli. Aplikacja nie tworzy niezweryfikowanego katalogu tych wartości.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Jedno wino na targi; Fair Points decydują o rankingu. P: Czy muszę zrobić Press Release wcześniej? O: Jeśli nie, deklarujesz na początku targów. P: Czy Fair Points znikają po targach? O: Stosuj regułę toru z planszy 2016 — nie mieszaj z Reserve.',
          },
        ],
      },
    ],
  },
  {
    id: 'bank-money',
    title: 'Bank i pieniądze',
    sections: [
      {
        id: 'money-main',
        title: 'Bagos i płynność',
        content: [
          { type: 'heading', text: 'Po co są bagos', level: 2 },
          {
            type: 'paragraph',
            text: 'Bagos finansują ruch, winnice, budynki, pracowników i inne efekty. Bez rezerwy nie wejdziesz na kluczowe pole Quadrelu. Sprzedaż wina jest podstawowym sposobem zwiększania gotówki w Special Vintage 2016.',
          },
          { type: 'heading', text: 'Kiedy pilnować płynności', level: 2 },
          {
            type: 'paragraph',
            text: 'Przed każdą akcją sprawdź, czy masz środki na ruch i na samą akcję. Jeśli spodziewasz się drogiego ruchu z rywalami na polu, najpierw zaplanuj sprzedaż albo tańsze sąsiednie pole. W 2016 nie przenoś pełnego konta i inwestycji z Reserve 2010.',
          },
          {
            type: 'bulletList',
            items: [
              'Pieniądze płacisz za część ruchów, winnice, budynki, pracowników i inne efekty.',
              'Sprzedaż wina jest podstawowym sposobem zwiększania gotówki.',
              'Pieniądze na koncie/banku i gotówka mogą podlegać różnym regułom wypłaty; korzystaj z pól banku planszy gracza.',
              'Nie można dobrowolnie wykonywać akcji, której pełnego kosztu nie jesteś w stanie zapłacić.',
              'Na koniec gry pieniądze mogą zostać przeliczone zgodnie z tabelą końcową, ale zwykle mają mniejszą wartość niż dobrze zaplanowany eksport.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład płynności',
            text: 'Masz 3 bagos i chcesz zbudować winiarnię na polu zajętym przez rywala. Ruch niesąsiadujący + opłata rywalowi zużywa 2 bagos, a koszt winiarni z kafla przekracza pozostałą 1 — rezygnujesz i najpierw sprzedajesz wino albo wybierasz sąsiednie pole rozwojowe.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: wyczerpać gotówkę na rozwój i zostać zablokowanym na Quadrelu, albo mylić uproszczoną ekonomię 2016 z kontem i inwestycjami Reserve 2010.',
          },
        ],
      },
      {
        id: 'sales-main',
        title: 'Sprzedaż wina',
        content: [
          { type: 'heading', text: 'Jak sprzedać wino', level: 2 },
          {
            type: 'numberedList',
            items: [
              'Wybierz wino i legalne pole rynku sprzedaży.',
              'Sprawdź minimalną wymaganą wartość.',
              'W razie potrzeby użyj dozwolonej renomy regionu.',
              'Odrzuć wino, umieść beczkę na rynku i otrzymaj wskazaną korzyść finansową.',
              'Podczas utrzymania zabierz dokładnie jedną własną beczkę z rynku, jeżeli masz tam jakąkolwiek.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład sprzedaży i utrzymania',
            text: 'Sprzedajesz wino na rynek i kładziesz beczkę. W kolejnym utrzymaniu zabierasz dokładnie jedną swoją beczkę — nie wszystkie naraz. To uwalnia slot i zwraca komponent do puli gracza zgodnie z regułą utrzymania.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: zabrać wszystkie beczki ze sprzedaży podczas utrzymania zamiast dokładnie jednej.',
          },
          {
            type: 'warning',
            text: 'Nie myl uproszczonej ekonomii 2016 z pełnym kontem i inwestycjami Reserve 2010.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Sprzedaż = gotówka; utrzymanie = jedna beczka. P: Czy mogę sprzedać bez progu? O: Nie. P: Czy pieniądze wygrywają grę? O: Rzadko — VP z eksportu i targów są ważniejsze.',
          },
        ],
      },
    ],
  },
  {
    id: 'experts',
    title: 'Eksperci winiarscy',
    sections: [
      {
        id: 'experts-main',
        title: 'Zatrudnianie i użycie',
        content: [
          { type: 'heading', text: 'Co robi ekspert i po co', level: 2 },
          {
            type: 'paragraph',
            text: 'Ekspert winiarski z gry podstawowej daje jednorazową zdolność oraz cechę używaną na targach. Zatrudniasz go akcją Wine Experts. To nie jest to samo co opcjonalny moduł Experts.',
          },
          {
            type: 'bulletList',
            items: [
              'Akcja Wine Experts pozwala zatrudnić ekspertów zgodnie z kosztem pola.',
              'Ekspert ma cechę używaną podczas targów oraz zdolność specjalną.',
              'Aby użyć zdolności, odwróć kafel eksperta zakryty.',
              'Zakryte kafle są odświeżane przed 4. i 6. rokiem.',
              'Ekspert odrzucony na potrzeby targów nie wraca przez samo odświeżenie.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład użycia',
            text: 'Zatrudniasz eksperta, później odwracasz go, by użyć zdolności w legalnym momencie wskazanym przez kafel. Przed 4. rokiem odwracasz ponownie zakryte kafle — ale ekspert odrzucony na targi nie wraca samym odświeżeniem.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: odświeżyć odrzuconych ekspertów albo mylić ekspertów podstawowych z modułem Experts.',
          },
          {
            type: 'note',
            text: 'Teksty zdolności odczytuj z kafli. Moduł Experts ma osobny rozdział w menu, gdy jest włączony.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Zatrudnij → użyj (odwróć) → cecha na targi. P: Czy odrzucony na targi wraca przy odświeżeniu? O: Nie. P: Czy to rozdział o module Experts? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'end-of-year',
    title: 'Koniec roku',
    sections: [
      {
        id: 'end-year-main',
        title: 'Procedura kontrolna',
        content: [
          { type: 'heading', text: 'Kiedy zamykasz rok', level: 2 },
          {
            type: 'paragraph',
            text: 'Po drugiej akcji i aktualizacji kolejności przechodzicie przez wspólny koniec roku. Ta checklista zapobiega pominięciu utrzymania albo odwróceniu kolejności starzenia i produkcji.',
          },
          {
            type: 'checklist',
            items: [
              'Zaktualizuj kolejność po drugiej akcji.',
              'Każdy gracz mający beczki w sprzedaży zabiera dokładnie jedną.',
              'Przesuń istniejące wina o jedno pole w prawo.',
              'Wyprodukuj jedno wino w każdej posiadłości z winnicą.',
              'Po roku 3., 5. lub 6. przeprowadź targi.',
              'Przesuń znacznik roku do kolejnego pola.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład kontroli',
            text: 'Ktoś mówi „produkujemy”. Zatrzymajcie się: czy zabrano po jednej beczce ze sprzedaży? Czy przesunięto stare wina? Dopiero potem produkcja. W roku 5 po produkcji są jeszcze targi.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: produkcja przed starzeniem albo pominięcie utrzymania beczek sprzedaży.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Kolejność → jedna beczka → starzenie → produkcja → targi jeśli rok. P: Czy w roku 2 są targi? O: Nie. P: Czy mogę zabrać dwie beczki? O: Nie, dokładnie jedną.',
          },
        ],
      },
    ],
  },
  {
    id: 'end-of-game',
    title: 'Koniec gry',
    sections: [
      {
        id: 'end-game-main',
        title: 'Po szóstych targach',
        content: [
          { type: 'heading', text: 'Kiedy gra się kończy', level: 2 },
          {
            type: 'paragraph',
            text: 'Gra kończy się po rozstrzygnięciu targów w 6. roku. Nie rozpoczynaj kolejnego utrzymania ani produkcji ponad etapy wskazane na torze.',
          },
          {
            type: 'numberedList',
            items: [
              'Dokończ pełne rozstrzygnięcie ostatnich targów.',
              'Rozlicz obszary eksportu i pozostałe cele końcowe.',
              'Rozlicz kafle punktacji, magnatów i moduły użyte w partii.',
              'Przelicz pozostałe zasoby tylko według oficjalnej tabeli końcowej.',
              'Dodaj wszystkie VP; najwyższy wynik wygrywa.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład zamknięcia',
            text: 'Po szóstych targach nie odkrywacie siódmego rocznika. Liczycie VP z eksportu, targów, beczek i tabeli końcowej. Dopiero wtedy ogłaszacie zwycięzcę.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: zaczynać siódmy rok albo punktować wszystkie pozostałe wina bez podstawy na planszy.',
          },
          {
            type: 'crossReference',
            chapterId: 'final-scoring',
            label: 'Szczegóły źródeł punktów',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Koniec = po 6. targach + rozliczenie. P: Czy jest rok 7? O: Nie. P: Czy każde wino punktuje? O: Tylko gdy efekt końcowy tak mówi.',
          },
        ],
      },
    ],
  },
  {
    id: 'final-scoring',
    title: 'Punktacja końcowa',
    sections: [
      {
        id: 'scoring-main',
        title: 'Źródła punktów',
        content: [
          { type: 'heading', text: 'Skąd biorą się VP', level: 2 },
          {
            type: 'paragraph',
            text: 'Punkty zbierasz przez całą partię i w rozliczeniu końcowym. Ta lista mówi, gdzie szukać VP — konkretne wartości pól i kafli odczytujesz z komponentów.',
          },
          {
            type: 'bulletList',
            items: [
              'Punkty zdobyte natychmiast podczas eksportu.',
              'Punkty z trzech targów.',
              'Punkty z beczek i pozycji w obszarze eksportu.',
              'Punkty z kafli punktacji, magnatów lub aktywnych modułów.',
              'Ewentualne przeliczenie pieniędzy i innych pozostałości zgodnie z planszą.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład sumowania',
            text: 'Masz VP z eksportów w trakcie gry, VP z trzech rankingów targowych oraz VP z pozycji beczek w eksporcie. Dopiero na końcu sprawdzasz tabelę przeliczenia pozostałości. Nie dodajesz „na oko” wartości leżących win.',
          },
          {
            type: 'warning',
            text: 'Nie punktuj automatycznie pozostałych win. Wina mają wartość ekonomiczną i użytkową, ale punktują tylko wtedy, gdy konkretny efekt końcowy tak mówi.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Eksport + targi + rozliczenie końcowe. P: Czy bagos = VP? O: Tylko według tabeli końcowej. P: Czy aplikacja zna wszystkie wartości kafli? O: Nie — PARTIAL; odczytuj nadruk.',
          },
        ],
      },
    ],
  },
  {
    id: 'guided-first-round',
    title: 'Prowadzona pierwsza runda',
    sections: [
      {
        id: 'guided-main',
        title: 'Jak poprowadzić pierwszy rok',
        content: [
          { type: 'heading', text: 'Po co ten rozdział', level: 2 },
          {
            type: 'paragraph',
            text: 'Pierwszy rok uczy rytmu, nie optymalnej strategii. Prowadzący nazywa głośno koszty ruchu, dwie akcje i koniec roku, zanim ktoś spróbuje „sprytnego” eksportu słabego wina.',
          },
          {
            type: 'numberedList',
            items: [
              'Pokaż wszystkim dziewięć pól Quadrelu i objaśnij koszty ruchu.',
              'Każdy gracz wybiera pierwszą akcję rozwojową: zwykle winnicę, winiarnię, pracownika lub piwnicę.',
              'Po pierwszej kolejce zaktualizuj kolejność tylko wtedy, gdy ktoś użył Pass / Press Release.',
              'W drugiej akcji zachęć do przygotowania produkcji albo płynności, nie do przypadkowego eksportu słabego wina.',
              'Podczas końca roku wykonaj głośno: utrzymanie, starzenie, produkcja.',
              'Po produkcji pokaż, jak odczytać aktualną wartość każdego wina.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład prowadzenia',
            text: 'Po drugiej akcji zatrzymajcie się przy utrzymaniu: „Czy ktoś ma beczki na rynku? Bierze dokładnie jedną.” Potem starzenie, potem produkcja. Dopiero wtedy ktoś może planować sprzedaż lub eksport na rok 2.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: pozwolić na cichy, pośpieszny koniec roku bez nazwania starzenia przed produkcją.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Rok 1 = nauka rytmu. P: Czy trzeba eksportować w roku 1? O: Nie. P: Czy Pass jest zły? O: Nie; uczy kolejności i Press Release.',
          },
        ],
      },
    ],
  },
  {
    id: 'beginner-strategy',
    title: 'Strategia dla początkujących',
    sections: [
      {
        id: 'strategy-main',
        title: 'Bezpieczne zasady',
        content: [
          { type: 'heading', text: 'Co robić na początku i dlaczego', level: 2 },
          {
            type: 'paragraph',
            text: 'Te wskazówki nie są regułami — pomagają uniknąć typowych pułapek pierwszej partii. Najpierw sprawna posiadłość i płynność, potem ekspansja i timing targów.',
          },
          {
            type: 'bulletList',
            items: [
              'Najpierw zbuduj sprawnie produkującą posiadłość, dopiero potem rozpraszaj się na regiony.',
              'Nie inwestuj całej gotówki; zostaw rezerwę na ruch i kluczową akcję.',
              'Piwnica jest cenna tylko wtedy, gdy rzeczywiście pozwolisz winom dojrzeć.',
              'Sprzedaż finansuje rozwój, eksport przynosi VP — potrzebujesz obu.',
              'Nie czekaj ze wszystkimi decyzjami targowymi do ostatniej chwili.',
              'Eksperci są elastyczni: ich zdolność może ratować tempo, a symbole wzmacniają targi.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład decyzji',
            text: 'Masz 2 bagos i chcesz wejść na odległe pole z rywalem. Policz koszt ruchu zanim klikniesz pole — jeśli po opłacie nie stać cię na akcję, wybierz sąsiednie pole rozwojowe.',
          },
          {
            type: 'commonMistake',
            text: 'Typowy błąd: budować piwnicę i natychmiast sprzedawać każde wino, zanim dojrzeje.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Jedna dobra posiadłość + płynność + eksport/targi. P: Czy to oficjalne reguły? O: Nie, to wskazówki. P: Czy trzeba brać wszystkich ekspertów? O: Nie.',
          },
        ],
      },
    ],
  },
  {
    id: 'common-mistakes',
    title: 'Najczęstsze błędy',
    sections: [
      {
        id: 'mistakes-main',
        title: 'Lista kontrolna',
        content: [
          { type: 'heading', text: 'Po co ta lista', level: 2 },
          {
            type: 'paragraph',
            text: 'Poniższe punkty zbierają błędy, które najczęściej psują pierwszą partię Special Vintage 2016. Przeczytajcie je przed rokiem 3 albo gdy ktoś „czuje, że coś poszło nie tak”.',
          },
          {
            type: 'bulletList',
            items: [
              'Pozostawienie znacznika na tym samym polu Quadrelu bez wyjątku Pass.',
              'Pominięcie opłaty dla innych graczy na docelowym polu.',
              'Mylenie renomy regionu ze stałym podniesieniem wartości kafla wina.',
              'Produkcja przed starzeniem.',
              'Zabranie wszystkich beczek ze sprzedaży podczas utrzymania zamiast dokładnie jednej.',
              'Wystawienie więcej niż jednego wina na te same targi.',
              'Odświeżenie odrzuconych ekspertów zamiast tylko odwrócenia zakrytych.',
              'Liczenie pieniędzy jak punktów 1:1 bez podstawy na planszy.',
            ],
          },
          {
            type: 'example',
            title: 'Przykład naprawy',
            text: 'Ktoś zabrał dwie beczki ze sprzedaży. Cofacie utrzymanie: każdy z beczkami zabiera dokładnie jedną, reszta zostaje. Potem dopiero starzenie i produkcja.',
          },
          {
            type: 'note',
            text: 'Odsyłacz: szczegóły procedur są w rozdziałach tematycznych; ta lista nie zastępuje reguł.',
          },
          { type: 'heading', text: 'Podsumowanie i mikro-FAQ', level: 2 },
          {
            type: 'paragraph',
            text: 'Sprawdź ruch, renomę, kolejność końca roku i targi. P: Czy jeden błąd unieważnia partię? O: Naprawcie stan i grajcie dalej. P: Gdzie więcej pytań? O: Ekran FAQ.',
          },
        ],
      },
    ],
  },
  {
    id: 'faq',
    title: 'FAQ',
    sections: [
      {
        id: 'faq-link',
        title: 'Pytania',
        content: [
          {
            type: 'paragraph',
            text: 'Rozbudowane odpowiedzi znajdują się w osobnym ekranie FAQ. Każde pytanie linkuje do właściwego rozdziału instrukcji. Mikro-FAQ na końcu rozdziałów odpowiadają na najczęstsze wątpliwości lokalnie.',
          },
        ],
      },
    ],
  },
  ...vinhosModuleChapters,
  {
    id: 'quick-reference',
    title: 'Szybka ściąga',
    sections: [
      {
        id: 'quick-link',
        title: 'W trakcie gry',
        content: [
          {
            type: 'paragraph',
            text: 'Ekran Szybka ściąga zawiera kolejność roku, koszty ruchu, produkcję, sprzedaż, eksport, targi i końcową punktację. Używaj go przy stole; pełne procedury zostają w rozdziałach powyżej.',
          },
        ],
      },
    ],
  },
] as RulebookChapter[];

export const vinhosRulebook: Rulebook = {
  chapters: [
    ...specialVintage2016Chapters.map((chapter) =>
      chapter.requiredVariantIds?.length
        ? chapter
        : { ...chapter, requiredVariantIds: ['special-vintage-2016' as const] }
    ),
    ...reserve2010Chapters,
  ],
};

export const vinhosChapterIds = vinhosRulebook.chapters.map((c) => c.id);
export const getChapterList = (rulebook: Rulebook = vinhosRulebook) =>
  rulebook.chapters.map((chapter, index) => ({
    id: chapter.id,
    title: chapter.title,
    index,
  }));
export const getChapterById = (
  chapterId: string,
  rulebook: Rulebook = vinhosRulebook
) => rulebook.chapters.find((c) => c.id === chapterId);
export const getChapterIndexById = (
  chapterId: string,
  rulebook: Rulebook = vinhosRulebook
) => rulebook.chapters.findIndex((c) => c.id === chapterId);
export function filterChaptersForConfiguration(
  rulebook: Rulebook,
  configOrModules: GameConfiguration | string[]
) {
  if (Array.isArray(configOrModules)) {
    return rulebook.chapters.filter(
      (ch) =>
        (!ch.requiredModuleIds?.length ||
          ch.requiredModuleIds.every((id) => configOrModules.includes(id))) &&
        (!ch.requiredVariantIds?.length ||
          ch.requiredVariantIds.includes('special-vintage-2016'))
    );
  }
  return filterByConfiguration(rulebook.chapters, configOrModules);
}
export function calculateReadingPercent(
  readChapterIds: string[],
  totalChapters: number
) {
  return totalChapters
    ? Math.round((readChapterIds.length / totalChapters) * 100)
    : 0;
}
